import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Col } from 'antd'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import { useForm, FormProvider } from 'react-hook-form'

import FormattedMessage from 'components/FormattedMessage'
import { setPreselectedFormData } from 'hooks/preselectedFormData'
import FormItem from 'components/Form/Item'
import InputNumber from 'components/Form/InputNumber'

import { setProvidersFilter } from 'store/modules/ProvidersFilter'
import { applyFilters, trackFilterUsage } from 'helpers/filters'
import { greaterThan } from 'helpers/validations'

import FilterButtonWithOverlay from '../components/FilterButtonWithOverlay'

import { RootState } from 'store/reducer'

import '../shared.less'
import './index.less'

const RoomFilter = () => {
  const form = useForm({ mode: 'onChange' })
  const intl = useIntl()
  const router = useRouter()
  const dispatch = useDispatch()
  const {
    hotelRooms,
    rooms,
  } = useSelector((state: RootState) => ({
    rooms: state.providersFilter.filters.rooms,
    hotelRooms: state.providersFilter.filters.hotelRooms,
  }), shallowEqual)

  const applyFiltersHandler = async () => {
    const isValid = await form.trigger()

    if (!isValid) { return false }

    const values = form.getValues()
    const clonedFilters = { ...values }

    trackFilterUsage(clonedFilters)

    return dispatch(setProvidersFilter(clonedFilters, (nextState) => {
      applyFilters(router)
      setPreselectedFormData({
        hotelRooms: nextState.filters.hotelRooms,
        rooms: nextState.filters.rooms,
      })
    }))
  }

  const resetFiltersHandler = () => {
    const emptyFilters = {
      hotelRooms: 0,
      rooms: 0,
    }

    dispatch(setProvidersFilter(emptyFilters, () => {
      applyFilters(router)
      setPreselectedFormData(emptyFilters)
    }))
  }

  const formatValue = (value: number | undefined) => value && value > 0 ? value : ''

  const filterButton = [{
    placeholder: <FormattedMessage id="newProviderFilters.rooms.button.label" />,
    value: (formatValue(rooms) || formatValue(hotelRooms)) && (
      <>
        {formatValue(rooms) && (
          <>
            {`${formatValue(rooms)} `}
            <FormattedMessage id="newProviderFilters.rooms.button.rooms.label" />
          </>
        )}
        {formatValue(rooms) && formatValue(hotelRooms) && ', '}
        {formatValue(hotelRooms) && (
          <>
            {`${formatValue(hotelRooms)} `}
            <FormattedMessage id="newProviderFilters.rooms.button.hotelRooms.label" />
          </>
        )}
        {!formatValue(rooms) && !formatValue(hotelRooms) && (
          <FormattedMessage id="newProviderFilters.rooms.button.label" />
        )}
      </>
    ),
  }]

  return (
    <FilterButtonWithOverlay
      applyHandler={applyFiltersHandler}
      resetHandler={resetFiltersHandler}
      className="provider-filter__pricing-filter"
      buttons={filterButton}
      onOpenCallback={() => {
        form.setValue('rooms', rooms)
        form.setValue('hotelRooms', hotelRooms)
      }}
    >
      <FormProvider {...form}>
        <Col span={20}>
            <FormItem
              name="rooms"
              label={<FormattedMessage id="newProviderFilters.rooms.rooms.label" />}
              className="provider-filter__form-input"
            >
              <InputNumber
                name="rooms"
                min={0}
                rules={{
                  validate: {
                    isPositive: greaterThan(0, intl.formatMessage({ id: 'errors.invalid' })),
                  },
                }}
                autoFocus={true}
                size="large"
                defaultValue={rooms}
                className="ant-input--with-border-radius"
              />
            </FormItem>
            <FormItem
              name="hotelRooms"
              label={<FormattedMessage id="newProviderFilters.rooms.hotelRooms.label" />}
              className="provider-filter__form-input"
            >
              <InputNumber
                name="hotelRooms"
                min={0}
                rules={{
                  validate: {
                    isPositive: greaterThan(0, intl.formatMessage({ id: 'errors.invalid' })),
                  },
                }}
                autoFocus={true}
                size="large"
                defaultValue={hotelRooms}
                className="ant-input--with-border-radius"
              />
            </FormItem>
          </Col>
      </FormProvider>
    </FilterButtonWithOverlay>
  )
}

export default RoomFilter
